/**
 * TanStack Query v5 hooks for issue reporting + Super-Admin usage analytics.
 * Mirrors useClientQueries.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  createIssueReportRequest,
  deleteIssueReportRequest,
  getIssueAttachmentBlobRequest,
  getIssueReportRequest,
  getUsageAnalyticsRequest,
  listIssueReportsRequest,
  updateIssueStatusRequest,
} from '@/services/issue-reports';
import type {
  CreateIssueReportInput,
  IssueReportDetailDto,
  IssueReportListItemDto,
  IssueReportListQuery,
  UpdateIssueStatusBody,
  UsageAnalyticsDto,
} from '@/types/issue-reports';
import type { PaginatedResult } from '@/types/roles';

// ── Queries ────────────────────────────────────────────────────────────────

export function useIssueReportsQuery(query: IssueReportListQuery = {}) {
  return useQuery<PaginatedResult<IssueReportListItemDto>, ApiError>({
    queryKey: ['issue-reports', 'list', query],
    queryFn: () => listIssueReportsRequest(query),
    staleTime: 15_000,
  });
}

export function useIssueReportQuery(id: number, enabled = true) {
  return useQuery<IssueReportDetailDto, ApiError>({
    queryKey: ['issue-reports', 'detail', id],
    queryFn: () => getIssueReportRequest(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

export function useUsageAnalyticsQuery(enabled = true) {
  return useQuery<UsageAnalyticsDto, ApiError>({
    queryKey: ['admin', 'usage-analytics'],
    queryFn: () => getUsageAnalyticsRequest(),
    enabled,
    staleTime: 30_000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

interface CreateIssueVars {
  readonly input: CreateIssueReportInput;
  readonly images: readonly File[];
  readonly onProgress?: (percent: number) => void;
}

export function useCreateIssueReportMutation() {
  const qc = useQueryClient();
  return useMutation<IssueReportDetailDto, ApiError, CreateIssueVars>({
    mutationFn: ({ input, images, onProgress }) =>
      createIssueReportRequest(input, images, onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issue-reports'] });
    },
  });
}

interface UpdateStatusVars {
  readonly id: number;
  readonly body: UpdateIssueStatusBody;
}

export function useUpdateIssueStatusMutation() {
  const qc = useQueryClient();
  return useMutation<IssueReportDetailDto, ApiError, UpdateStatusVars>({
    mutationFn: ({ id, body }) => updateIssueStatusRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issue-reports'] });
    },
  });
}

export function useDeleteIssueReportMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, number>({
    mutationFn: async (id) => {
      await deleteIssueReportRequest(id);
      return undefined;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issue-reports'] });
    },
  });
}

/** Fetch an attachment blob and expose it as an object URL (revoked by the consumer). */
export function useIssueAttachmentBlobQuery(reportId: number, attachmentId: number, enabled = true) {
  return useQuery<Blob, ApiError>({
    queryKey: ['issue-reports', 'attachment', reportId, attachmentId],
    queryFn: () => getIssueAttachmentBlobRequest(reportId, attachmentId),
    enabled: enabled && reportId > 0 && attachmentId > 0,
    staleTime: 5 * 60_000,
  });
}
